import { CliError } from "../../lib/cli/cli-error";
import { LIMITS } from "@/commands/answers/collect-answers-constants";
import type { Answers } from "@/commands/answers/answers";
import type { Answer } from "@/commands/answer/answer";
import { validateAnswer } from "@/commands/answer/validate-answer";
import { parseAnswersFlag } from "@/commands/answers/parse-answers-flag";
import type { ParsedArgs } from "@/lib/parsed/parsed-args";

/** `--option <questionId>=1,2` and `--text <questionId>=…`, or `--answers <json>`. */
export function collectAnswers(parsed: ParsedArgs): Answers {
  if (parsed.flags.answers) {
    return parseAnswersFlag(parsed.flags.answers);
  }

  const byQuestion = new Map<string, Answer>();

  const put = (pair: string, apply: (entry: Answer) => void) => {
    const equalsAt = pair.indexOf("=");
    if (equalsAt < 0) {
      throw new CliError(`Expected <questionId>=<value>, got "${pair}".`, 2);
    }
    const questionId = pair.slice(0, equalsAt);
    const entry = byQuestion.get(questionId) ?? { questionId };
    apply(entry);
    byQuestion.set(questionId, entry);
  };

  for (const pair of parsed.repeated.option ?? []) {
    put(pair, (entry) => {
      entry.selectedOptionIds = pair
        .slice(pair.indexOf("=") + 1)
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);
    });
  }

  for (const pair of parsed.repeated.text ?? []) {
    put(pair, (entry) => {
      entry.freeText = pair.slice(pair.indexOf("=") + 1);
    });
  }

  const answers = [...byQuestion.values()];
  if (answers.length === 0) {
    throw new CliError(
      "Nothing to submit. Use --option <questionId>=<ids>, --text <questionId>=<answer>, or --answers <json>.",
      2,
    );
  }
  if (answers.length > LIMITS.ANSWERS) {
    throw new CliError(
      `More than ${LIMITS.ANSWERS} answers in one submission.`,
      2,
    );
  }

  for (const answer of answers) {
    validateAnswer(answer, `--option/--text ${answer.questionId}`);
  }

  return answers;
}
